import {
  IMPACT_CARRY_CONCEPT_ID,
  IMPACT_STRATEGY_CONCEPT_ID,
} from '~/constants/concepts'
import type { ImpactCarryEvidenceLink } from '~/components/ix/card/ImpactCarryEvidenceList.vue'
import type { ImpactType, MetricAssignmentType } from '~/types/types'

export const REVIEW_SNAPSHOT_SCHEMA_VERSION = 1

export type ReviewSnapshotStrategy = {
  impactId: number
  submitDate?: string | null
  isComplete: boolean
  fields: {
    companyVision?: string | null
    problemStatement?: string | null
    targetGroup?: string | null
    inputsActivities?: string | null
    solutionsOutput?: string | null
    shortTermOutcomes?: string | null
    longTermImpacts?: string | null
    riskReboundEffects?: string | null
  }
}

export type ReviewSnapshotCarry = {
  impactId: number
  submitDate?: string | null
  initialInvestmentDate?: string | null
  startingYear?: number | null
  durationYears?: number | null
  carryRemarks?: string | null
  evidence: ImpactCarryEvidenceLink[]
  metricAssignments: MetricAssignmentType[]
}

export type ReviewVentureSnapshotPayload = {
  schemaVersion: number
  capturedAt: string
  company: {
    ventureId: string
    name?: string | null
  }
  strategy?: ReviewSnapshotStrategy | null
  carry?: ReviewSnapshotCarry | null
  meta: {
    reviewRequestId: string
    reviewEventId: string
    eventType: string
    reviewerUserId?: string | null
  }
}

export function parseReviewVentureSnapshot(
  raw: unknown,
): ReviewVentureSnapshotPayload | null {
  let value: unknown = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object') return null
  const payload = value as ReviewVentureSnapshotPayload
  if (payload.schemaVersion !== REVIEW_SNAPSHOT_SCHEMA_VERSION) return null
  if (!payload.company?.ventureId) return null
  return payload
}

/**
 * Live getImpactGoal does not request completionStatus.aggregationView.
 * Cards recompute period tables from metricValues; frozen aggregationView
 * uses server running-total semantics and mis-renders partial reporting.
 */
export function stripSnapshotMetricAssignmentForDisplay(
  assignment: MetricAssignmentType,
): MetricAssignmentType {
  if (!assignment.completionStatus) return assignment
  const completionStatus = { ...assignment.completionStatus } as Record<string, unknown>
  delete completionStatus.aggregationView
  return {
    ...assignment,
    completionStatus: completionStatus as MetricAssignmentType['completionStatus'],
  }
}

export function snapshotToStrategyImpact(
  strategy: ReviewSnapshotStrategy,
): ImpactType {
  const { fields } = strategy
  return {
    id: strategy.impactId,
    conceptId: IMPACT_STRATEGY_CONCEPT_ID,
    submitDate: strategy.submitDate ?? null,
    companyVision: fields.companyVision ?? null,
    problemStatement: fields.problemStatement ?? null,
    targetGroup: fields.targetGroup ?? null,
    inputsActivities: fields.inputsActivities ?? null,
    solutionsOutput: fields.solutionsOutput ?? null,
    shortTermOutcomes: fields.shortTermOutcomes ?? null,
    longTermImpacts: fields.longTermImpacts ?? null,
    riskReboundEffects: fields.riskReboundEffects ?? null,
  } as ImpactType
}

export function snapshotToCarryImpact(carry: ReviewSnapshotCarry): ImpactType {
  return {
    id: carry.impactId,
    conceptId: IMPACT_CARRY_CONCEPT_ID,
    submitDate: carry.submitDate ?? null,
    initialInvestmentDate: carry.initialInvestmentDate ?? null,
    startingYear: carry.startingYear ?? null,
    durationYears: carry.durationYears ?? null,
    carryRemarks: carry.carryRemarks ?? null,
    metricAssignments: (carry.metricAssignments ?? []).map(
      stripSnapshotMetricAssignmentForDisplay,
    ),
  } as ImpactType
}

export function snapshotCarryEvidenceLinks(
  carry: ReviewSnapshotCarry | null | undefined,
): ImpactCarryEvidenceLink[] {
  return carry?.evidence ?? []
}
